/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext } from 'react';
import classNames from 'classnames';
import Link from 'next/link';

import { BASE_CHOWNOW_URL } from '@chownow/cn-web-utils/paths';

import { CustomerContext } from 'context/customer';
import { handleAppLinkClick } from 'helpers/mParticle';
import useIsBrowser from 'hooks/useIsBrowser';
import usePrevious from 'hooks/usePrevious';

import LogoMark from 'components/shared/SVG/LogoMark';
import LogoStandard from 'components/shared/SVG/LogoStandard';
import SidebarMenu from 'components/shared/SideBarMenu/SideBarMenu';
import AutocompleteWrapper from 'components/views/search/components/AutocompleteWrapper/AutocompleteWrapper';
import AuthDropdown from './AuthDropdown/AuthDropdown';

import styles from './Nav.module.scss';

interface NavProps {
  /** Handles scrolling to the acquisition ref on Homepage */
  handleScroll?: React.MouseEventHandler<HTMLButtonElement>;
  /** Boolean to display the home nav configuration */
  homeNav?: boolean;
}

export default function Nav({ handleScroll = () => undefined, homeNav = false }: NavProps) {
  const { user, hasFetched } = useContext(CustomerContext);
  const isBrowser = useIsBrowser();
  // We want to delay rendering of certain components until the client has mounted AND we've fetched user data.
  // This prevents layout thrashing on load
  const clientReady = isBrowser && hasFetched;
  // We should only "fade" the text once refresh, of possible. We use a ref to track that so as not
  // to update state -> cause a render -> interrupt the CSS fade
  const wasPreviouslyAnimated = usePrevious(hasFetched);

  const onDesktopDownloadClick = (event) => {
    // Had to pass an argument to avoid a Typescript error
    handleScroll(event);
    handleAppLinkClick({
      user,
      source: 'Nav',
    });
  };

  return (
    <nav
      className={classNames(styles.navbar, {
        [styles.homeNav]: homeNav,
        [styles.searchNav]: !homeNav,
      })}
    >
      {homeNav ? (
        <>
          <Link href="/" passHref tabIndex={0} className={styles.fullLogo}>
            <LogoStandard />
          </Link>
          {clientReady && (
            <div className={styles.homeLinks}>
              <a
                href="https://chownow.app.link/Download-CN-App-Web" // only for mobile, desktop must scroll to dowload app section
                className={styles.downloadLink}
                onClick={() => {
                  handleAppLinkClick({
                    user,
                    source: 'Nav',
                  });
                }}
                target="_blank"
                rel="noreferrer"
              >
                Download App
              </a>
              <button type="button" className={styles.downloadBtn} onClick={onDesktopDownloadClick}>
                Download App
              </button>
              <a
                href={`${BASE_CHOWNOW_URL}/?utm_medium=desktop_nav&utm_source=marketplace_web`}
                className={styles.restaurantsLink}
                target="_blank"
                rel="noreferrer"
              >
                For Restaurants
              </a>
            </div>
          )}
        </>
      ) : (
        <>
          <Link href="/" passHref tabIndex={0} className={styles.iconLogo}>
            <LogoMark />
          </Link>
          <div className={styles.restaurantSearch}>
            <AutocompleteWrapper />
          </div>
        </>
      )}
      <SidebarMenu className={styles.sidebarMenu} />
      {clientReady && (
        <div className={wasPreviouslyAnimated ? styles.inanimateAuth : styles.animateAuth}>
          <AuthDropdown />
        </div>
      )}
    </nav>
  );
}
